import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { COLOR_PRESETS, matchesRule, parseDateValue, rulesFor } from './formats';
import { ChartAggregation, ChartConfig, ColorPreset, ColumnDef, FormatsState } from './types';
import { cn } from '@/lib/utils';

export type ChartBackground = 'white' | 'transparent' | ColorPreset;

interface ChartViewProps<T extends { id: string }> {
  rows: T[];
  columns: ColumnDef<T>[];
  formats: FormatsState;
  config: ChartConfig | undefined;
  background?: ChartBackground;
}

const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
];

const MS_PER_DAY = 86_400_000;

// Translate a color-preset name (yellow/red/green/...) to a hex used by recharts
const PRESET_HEX: Record<string, string> = {
  yellow: '#facc15',
  red: '#ef4444',
  green: '#22c55e',
  blue: '#3b82f6',
  purple: '#a855f7',
  orange: '#f97316',
  pink: '#ec4899',
  gray: '#94a3b8',
};

interface Bucket {
  key: string;
  /** Sort key — for date axes this is a timestamp, otherwise the value or value count. */
  sortKey: number | string;
  rows: any[];
  value: number;
  /** Optional row used for conditional color matching — represents the bucket. */
  sample: any | null;
}

export function ChartView<T extends { id: string }>({
  rows,
  columns,
  formats,
  config,
  background = 'white',
}: ChartViewProps<T>) {
  const bgClass =
    background === 'white'
      ? 'bg-white border border-slate-200'
      : background === 'transparent'
        ? ''
        : cn('border', COLOR_PRESETS[background].bg, COLOR_PRESETS[background].border);
  const xCol = useMemo(
    () => (config ? columns.find((c) => c.id === config.xAxisColumn) ?? null : null),
    [columns, config]
  );
  const yCol = useMemo(
    () => (config?.valueColumn ? columns.find((c) => c.id === config.valueColumn) ?? null : null),
    [columns, config]
  );

  const data = useMemo<Bucket[]>(() => {
    if (!xCol || !config) return [];
    const isDateAxis = xCol.dataType === 'date';
    const map = new Map<string, Bucket>();

    for (const r of rows) {
      let key: string;
      let sortKey: number | string;

      if (isDateAxis) {
        const ts = xCol.numericValue
          ? xCol.numericValue(r as T)
          : parseDateValue(xCol.accessor(r as T));
        if (!Number.isFinite(ts)) continue;
        const day = Math.floor(ts / MS_PER_DAY) * MS_PER_DAY;
        key = new Date(day).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        });
        sortKey = day;
      } else {
        key = xCol.accessor(r as T) || '(empty)';
        sortKey = key;
      }

      let bucket = map.get(key);
      if (!bucket) {
        bucket = { key, sortKey, rows: [], value: 0, sample: r };
        map.set(key, bucket);
      }
      bucket.rows.push(r);
    }

    const buckets = Array.from(map.values());
    for (const b of buckets) {
      b.value = aggregate(b.rows, config.aggregation, yCol);
    }

    if (isDateAxis) {
      buckets.sort((a, b) => (a.sortKey as number) - (b.sortKey as number));
    } else {
      buckets.sort((a, b) => b.value - a.value);
    }
    return buckets;
  }, [rows, xCol, yCol, config]);

  const colorFor = (bucket: Bucket, index: number): string => {
    if (config?.useConditionalColors && xCol && bucket.sample) {
      for (const rule of rulesFor(formats, xCol.id)) {
        if (matchesRule(bucket.sample, xCol, rule)) {
          const preset = rule.bgColor ?? rule.iconColor ?? rule.textColor ?? rule.color;
          if (preset) return PRESET_HEX[preset] ?? PALETTE[index % PALETTE.length];
          break;
        }
      }
    }
    return PALETTE[index % PALETTE.length];
  };

  if (!config || !xCol) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-sm text-slate-400">
        Open the display options (gear icon) and pick a column for the X-axis.
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-sm text-slate-400">
        No data to chart with the current filter.
      </div>
    );
  }

  const yLabel = config.aggregation === 'count'
    ? 'Count'
    : `${capitalize(config.aggregation)} of ${yCol?.label ?? '—'}`;

  const xTickAngled = data.length > 8;
  const xAxisHeight = xTickAngled ? 48 : 20;

  return (
    <div className={cn('rounded-xl overflow-hidden flex flex-col h-full min-h-[160px]', bgClass)}>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          {config.type === 'pie' ? (
            <PieChart margin={{ top: 12, right: 4, bottom: 4, left: 4 }}>
              <Tooltip />
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align="right"
                wrapperStyle={{ fontSize: 12 }}
              />
              <Pie
                data={data}
                dataKey="value"
                nameKey="key"
                cx="40%"
                cy="50%"
                outerRadius="85%"
                label={(p: any) => {
                  const k = p?.key ?? '';
                  const pct =
                    typeof p?.percent === 'number' ? `${(p.percent * 100).toFixed(0)}%` : '';
                  return pct ? `${k} (${pct})` : String(k);
                }}
              >
                {data.map((b, i) => (
                  <Cell key={i} fill={colorFor(b, i)} />
                ))}
              </Pie>
            </PieChart>
          ) : config.type === 'line' ? (
            <LineChart data={data} margin={{ top: 14, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="key"
                tick={{ fontSize: 11, fill: '#64748b' }}
                angle={xTickAngled ? -35 : 0}
                textAnchor={xTickAngled ? 'end' : 'middle'}
                height={xAxisHeight}
                tickMargin={2}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                width={32}
                tickMargin={2}
              />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke={PALETTE[0]}
                strokeWidth={2}
                name={yLabel}
                dot
              />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 14, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="key"
                tick={{ fontSize: 11, fill: '#64748b' }}
                angle={xTickAngled ? -35 : 0}
                textAnchor={xTickAngled ? 'end' : 'middle'}
                height={xAxisHeight}
                tickMargin={2}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                width={32}
                tickMargin={2}
              />
              <Tooltip />
              <Bar dataKey="value" name={yLabel} radius={[4, 4, 0, 0]}>
                {data.map((b, i) => (
                  <Cell key={i} fill={colorFor(b, i)} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function aggregate<T>(
  rows: T[],
  mode: ChartAggregation,
  valueCol: ColumnDef<T> | null
): number {
  if (mode === 'count') return rows.length;
  if (!valueCol) return 0;
  const values = rows
    .map((r) =>
      valueCol.numericValue ? valueCol.numericValue(r) : parseFloat(valueCol.accessor(r))
    )
    .filter((v) => Number.isFinite(v));
  if (values.length === 0) return 0;
  switch (mode) {
    case 'sum': return values.reduce((acc, v) => acc + v, 0);
    case 'avg': return values.reduce((acc, v) => acc + v, 0) / values.length;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// COLOR_PRESETS imported only to keep the rule-color → hex mapping aligned
// with the rest of the app; not consumed directly here.
void COLOR_PRESETS;
